// deps
import { useCallback, useContext } from "react";

// internal-deps
import { Err, Ok, Result } from "@swiggy-private/js-fp";
import {
    IUpdateGroupCartOrderRequestPayload,
    UpdateGroupCartOrderResponse,
    ApiMenuCartItem,
} from "@swiggy-private/cart-types";

// apis
import { updateGroupCartOrderItemsCall } from "@cart-client/apis/GroupCartOrder";

// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";

// helpers
import {
    formUpdateOrderCartRequestPayload,
    getGroupOrderErrorPayload,
    getLockedGroupCartErrorHandling,
} from "@cart-client/helpers/GroupCartOrder";
import { isEmpty } from "@swiggy-private/js-utils";

// contexts
import { GroupOrderCartContext, GroupCartActionTypes } from "@swiggy-private/checkout-core";

// reducers
import { OOPS_SOMETHING_WENT_WRONG } from "@cart-client/constants/TextDict";

function updateGroupCartOrderItems(
    body: IUpdateGroupCartOrderRequestPayload,
): Promise<Result<UpdateGroupCartOrderResponse, Error | ApiResponse>> {
    return updateGroupCartOrderItemsCall(body)
        .then(groupOrderCartResponse => {
            return Ok(groupOrderCartResponse);
        })
        .catch(err => {
            return Err(err);
        });
}

const usePerformGroupOrderCartUpdate = (
    groupCartId: string,
    enablePolling: (resumeWithDelay?: boolean) => void,
    disablePolling: VoidFunction,
) => {
    const { state, setState } = useContext(GroupOrderCartContext);

    const updateGroupOrderCartData = useCallback(
        async (userId: string, cartItems: ApiMenuCartItem[]) => {
            try {
                setState({
                    type: GroupCartActionTypes.GROUP_CART_STATE_UPDATE_IN_PROGRESS,
                });
                const reqPayload = formUpdateOrderCartRequestPayload(cartItems, userId, state.data, groupCartId);

                // stop polling
                disablePolling();

                const data = await updateGroupCartOrderItems(reqPayload);

                if (data.isErr()) {
                    throw data;
                }

                const groupCartOrderData = data.unwrap();

                // Do not update the page state if the new set of data is errored/empty
                const updatedData = !isEmpty(groupCartOrderData.data) ? groupCartOrderData.data : state.data;
                setState({
                    type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                    payload: {
                        data: updatedData,
                        error: getGroupOrderErrorPayload(groupCartOrderData),
                        softError: getLockedGroupCartErrorHandling(groupCartOrderData),
                    },
                });

                // re-enable back polling with a delay of 3s, since latest data was already synced with update-cart call
                enablePolling(true);
            } catch (err) {
                // only show soft error when update api fails and enable polling with a delay
                setState({
                    type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                    payload: {
                        softError: OOPS_SOMETHING_WENT_WRONG,
                    },
                });
                enablePolling(true);
            }
        },
        [setState, state, groupCartId, disablePolling, enablePolling],
    );

    return {
        updateGroupOrderCartData,
    };
};

export default usePerformGroupOrderCartUpdate;
